// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useState } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  withStyles,
  Typography,
  Grid,
  Checkbox,
  FormHelperText,
} from '@material-ui/core';
import { get, some } from 'lodash';
import { arrayOf, shape, string, bool, func } from 'prop-types';

import { getI18n, getI18nFormattedMessage } from '../../../../utils/intl';
import {
  AlertMessages,
  ErrorContainer,
  FormTextField,
  FormActionButtons,
  Switch,
} from '../../../../components';
import { getIsProviderNameUnique } from '../../../../reducers/authenticationSettings';
import {
  checkProviderNameUnique,
  setAuthSettings,
  updateAuthScheme,
  saveAuthScheme,
} from '../../../../actions/authenticationSettings';
import { hasError } from '../../../../utils';
import styles from '../styles';

export const Configuration = (props) => {
  const {
    classes,
    errors,
    authDetails,
    uuid,
    onCancelDetails,
    tabValue,
  } = props;

  const intl = getI18n(useIntl());

  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [minPassword, setMinPassword] = useState('');
  const [isMinPasswordError, setIsMinPasswordError] = useState(false);
  const [maxPassword, setMaxPassword] = useState('');
  const [isMaxPasswordError, setIsMaxPasswordError] = useState(false);
  const [uppercaseChars, setUppercaseChars] = useState('');
  const [isUppercaseCharsError, setIsUppercaseCharsError] = useState(false);
  const [isCheckUppercase, setIsCheckUppercase] = useState(false);
  const [lowercaseChars, setLowercaseChars] = useState('');
  const [isLowercaseCharsError, setIsLowercaseCharsError] = useState(false);
  const [isCheckLowercase, setIsCheckLowercase] = useState(false);
  const [numericChars, setNumericChars] = useState('');
  const [isNumericCharsError, setIsNumericCharsError] = useState(false);
  const [isCheckNumeric, setIsCheckNumeric] = useState(false);
  const [specialChars, setSpecialChars] = useState('');
  const [isSpecialCharsError, setIsSpecialCharsError] = useState(false);
  const [isCheckSpecial, setIsCheckSpecial] = useState(false);
  const [maxFailedAttempts, setMaxFailedAttempts] = useState('');
  const [isMaxFailedAttemptsError, setIsMaxFailedAttemptsError] = useState('');
  const [isCheckMaxFailed, setIsCheckMaxFailed] = useState(false);
  const [passwordExpiry, setPasswordExpiry] = useState('');
  const [isPasswordExpiryError, setIsPasswordExpiryError] = useState(false);
  const [isCheckPasswordExpiry, setIsCheckPasswordExpiry] = useState(false);
  const [passwordHistory, setPasswordHistory] = useState('');
  const [isPasswordHistoryError, setIsPasswordHistoryError] = useState(false);
  const [isCheckPasswordHistory, setIsCheckPasswordHistory] = useState(false);
  const [passwordLimit, setPasswordLimit] = useState('');
  const [isPasswordLimitError, setIsPasswordLimitError] = useState(false);
  const [isCheckPasswordLimit, setIsCheckPasswordLimit] = useState(false);
  const [enhancedSecurity, setEnhancedSecurity] = useState(false);
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  useEffect(() => {
    if(tabValue === 'configuration-tab') {
      if(get(authDetails, 'passwordPolicies')) {
        const regexConfig = get(authDetails, 'passwordPolicies.regexConfig');
        const passwordConfig = get(authDetails, 'passwordPolicies.passwordConfig');
        const accountLockConfig = get(authDetails, 'passwordPolicies.accountLockConfig');
        setMinPassword(get(regexConfig, 'MINIMUM_LENGTH.value'));
        setMaxPassword(get(regexConfig, 'MAXIMUM_LENGTH.value'));
        setUppercaseChars(get(regexConfig, 'UPPERCASE.value'));
        setIsCheckUppercase(get(regexConfig, 'UPPERCASE.enabled'))
        setLowercaseChars(get(regexConfig, 'LOWERCASE.value'));
        setIsCheckLowercase(get(regexConfig, 'LOWERCASE.enabled'))
        setNumericChars(get(regexConfig, 'NUMBER.value'));
        setIsCheckNumeric(get(regexConfig, 'NUMBER.enabled'));
        setSpecialChars(get(regexConfig, 'SYMBOL.value'));
        setIsCheckSpecial(get(regexConfig, 'SYMBOL.enabled'))
        setMaxFailedAttempts(get(accountLockConfig, 'MAXIMUM_FAILEDATTEMPTS.value'));
        setIsCheckMaxFailed(get(accountLockConfig, 'MAXIMUM_FAILEDATTEMPTS.enabled'));
        setPasswordExpiry(get(passwordConfig, 'PASSWORD_EXPIRY.value'));
        setIsCheckPasswordExpiry(get(passwordConfig, 'PASSWORD_EXPIRY.enabled'));
        setPasswordHistory(get(passwordConfig, 'PASSWORD_HISTORY.value'));
        setIsCheckPasswordHistory(get(passwordConfig, 'PASSWORD_HISTORY.enabled'));
        setPasswordLimit(get(passwordConfig, 'LIMIT_FYP_ATTEMPTS.value'));
        setIsCheckPasswordLimit(get(passwordConfig, 'LIMIT_FYP_ATTEMPTS.enabled'));
        setEnhancedSecurity(get(authDetails, 'advancedConfigurations.enhancedPasswordSecurity') === 'no' ? false : true);
      }
      if(localStorage.getItem('isUnSavedChanges') === 'true') {
        setIsUnsavedChanges(true);
      } else {
        setIsUnsavedChanges(false);
      }
    }
  }, [authDetails, tabValue]);

  useEffect(() => {
    if(isUnsavedChanges) {
      const hasSave = false;
      setIsSaveDisabled(hasSave);
    }
  }, [isUnsavedChanges, tabValue]);

  const handleChange = (fieldName, value, length) => {
    const uiError = hasError(value, true, length);
    if(fieldName === 'maxPassword') {
      setMaxPassword(value);
      value < 8 || value > 60 ? setIsMaxPasswordError(true) :
      setIsMaxPasswordError(uiError);
    }
    else if(fieldName === 'minPassword') {
      setMinPassword(value);
      value < 8 || value > 60 ? setIsMinPasswordError(true) :
      setIsMinPasswordError(uiError);
    } else if(fieldName === 'uppercaseChars') {
      setUppercaseChars(value);
      setIsUppercaseCharsError(uiError);
    } else if(fieldName === 'lowercaseChars') {
      setLowercaseChars(value);
      setIsLowercaseCharsError(uiError);
    } else if(fieldName === 'numericChars') {
      setNumericChars(value);
      setIsNumericCharsError(uiError);
    } else if(fieldName === 'specialChars') {
      setSpecialChars(value);
      setIsSpecialCharsError(uiError);
    } else if(fieldName === 'maxFailedAttempts') {
      setMaxFailedAttempts(value);
      setIsMaxFailedAttemptsError(uiError);
    } else if(fieldName === 'passwordExpiry') {
      setPasswordExpiry(value);
      setIsPasswordExpiryError(uiError);
    } else if(fieldName === 'passwordHistory') {
      setPasswordHistory(value);
      setIsPasswordHistoryError(uiError);
    } else if(fieldName === 'passwordLimit') {
      setPasswordLimit(value);
      setIsPasswordLimitError(uiError);
    }

    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
  }

  const onSave = () => {

    if (!some([
      isMinPasswordError, isMaxPasswordError, isUppercaseCharsError, isLowercaseCharsError,
      isNumericCharsError, isSpecialCharsError, isMaxFailedAttemptsError,
      isPasswordExpiryError, isPasswordHistoryError,
    ])) {
      setNotificationStatus('');
      setNotificationMessage('');
      setIsUnsavedChanges(false);
      const data = { ...authDetails, ...{ ConfigurationParams : { },
        passwordPolicies: {
          accountLockConfig: {
            MAXIMUM_FAILEDATTEMPTS: { enabled: isCheckMaxFailed, value: maxFailedAttempts },
          },
          passwordConfig: {
            LIMIT_FYP_ATTEMPTS: { enabled: isCheckPasswordLimit, value: passwordLimit },
            PASSWORD_EXPIRY: { enabled: isCheckPasswordExpiry, value: passwordExpiry },
            PASSWORD_HISTORY: { enabled: isCheckPasswordHistory, value: passwordHistory },
          },
          regexConfig: {
            LOWERCASE: { enabled: isCheckLowercase, value: lowercaseChars },
            MAXIMUM_LENGTH: { enabled: true, value: maxPassword },
            MINIMUM_LENGTH: { enabled: true, value: minPassword },
            NUMBER: { enabled: isCheckNumeric, value: numericChars },
            SYMBOL: { enabled: isCheckSpecial, value: specialChars },
            UPPERCASE: { enabled: isCheckUppercase, value: uppercaseChars },
          },
        },
        advancedConfigurations: {
          enhancedPasswordSecurity: enhancedSecurity ? 'yes' : 'no',
        },
      } };
      if (uuid) {
        props.updateAuthScheme(uuid, data);
      }
    }
  }

  const onCancel = () => {
    onCancelDetails(isUnsavedChanges);
  }


  return(
    <div
      id="auth-settings-config-container"
      data-apim-test="auth-settings-config-container"
      className={classes.pageContainer}
    >
      {notificationMessage &&
        <AlertMessages
          id="auth-settings-config-notifications"
          message={notificationMessage}
          variant={notificationStatus}
          onClose={() => {
            setNotificationMessage('');
            setNotificationStatus('');
          }}
        />
      }
      {errors.length > 0 &&
        <ErrorContainer errors={errors} />
      }
      <Typography
        variant="h1"
        className={classes.pageTitle}
        id="auth-settings-config-page-title"
        data-apim-test="auth-settings-config-page-title"
      >
        {intl.getI18nMessage('label.auth.password.policy')}
      </Typography>
      <Grid container spacing={3} className={classes.gridContainer}>
        <Grid md={12} item>
            <Typography
              variant="h4"
              id="auth-settings-config-password"
              data-apim-test="auth-settings-config-password"
            >
              {getI18nFormattedMessage('label.auth.password.policy')}
            </Typography>
            <div className={classes.configCheckContainer}>
              <Checkbox
                checked={true}
                disabled={true}
                id={'auth-settings-min-password-check'}
                data-apim-test={'auth-settings-min-password-check'}
              />
            </div>
            <div className={classes.configCellContainer}>
              <FormTextField
                id={'auth-settings-min-password'}
                data-apim-test={'auth-settings-min-password'}
                name={intl.getI18nMessage('label.auth.minimum.password.length')}
                value={minPassword}
                type={'number'}
                error={isMinPasswordError}
                handleChange={(value) => handleChange('minPassword', value, 2)}
                helperText={intl.getI18nMessage('label.auth.min.value.help.text')}
                fieldContainerClass={classes.textConfigField}
              />
            </div>
            <div className={classes.configCheckContainer}>
              <Checkbox
                checked={true}
                disabled={true}
                id={'auth-settings-max-password-check'}
                data-apim-test={'auth-settings-max-password-check'}
              />
            </div>
            <div className={classes.configCellContainer}>
              <FormTextField
                id={'auth-settings-max-password'}
                data-apim-test={'auth-settings-max-password'}
                name={intl.getI18nMessage('label.auth.maximum.password.length')}
                value={maxPassword}
                type={'number'}
                error={isMaxPasswordError}
                handleChange={(value) => handleChange('maxPassword', value, 2)}
                helperText={intl.getI18nMessage('label.auth.max.value.help.text')}
                fieldContainerClass={classes.textConfigField}
              />
            </div>
            <div className={classes.configCheckContainer}>
              <Checkbox
                checked={isCheckUppercase}
                onChange={() => setIsCheckUppercase(!isCheckUppercase)}
                id={'auth-settings-uppercase-chars-check'}
                data-apim-test={'auth-settings-uppercase-chars-check'}
              />
            </div>
            <div className={classes.configCellContainer}>
              <FormTextField
                id={'auth-settings-uppercase-chars'}
                data-apim-test={'auth-settings-uppercase-chars'}
                name={intl.getI18nMessage('label.auth.uppercase.characters')}
                value={uppercaseChars}
                type={'number'}
                error={isUppercaseCharsError}
                handleChange={(value) => handleChange('uppercaseChars', value, 2)}
                helperText={intl.getI18nMessage('label.auth.chars.help.text')}
                fieldContainerClass={classes.textConfigField}
                disabled={!isCheckUppercase}
              />
            </div>
            <div className={classes.configCheckContainer}>
              <Checkbox
                checked={isCheckLowercase}
                onChange={() => setIsCheckLowercase(!isCheckLowercase)}
                id={'auth-settings-lowercase-chars-check'}
                data-apim-test={'auth-settings-lowercase-chars-check'}
              />
            </div>
            <div className={classes.configCellContainer}>
              <FormTextField
                id={'auth-settings-lowercase-chars'}
                data-apim-test={'auth-settings-lowercase-chars'}
                name={intl.getI18nMessage('label.auth.lower.characters')}
                value={lowercaseChars}
                type={'number'}
                error={isLowercaseCharsError}
                handleChange={(value) => handleChange('lowercaseChars', value, 2)}
                fieldContainerClass={classes.textConfigField}
                helperText={intl.getI18nMessage('label.auth.chars.help.text')}
                disabled={!isCheckLowercase}
              />
            </div>
            <div className={classes.configCheckContainer}>
              <Checkbox
                checked={isCheckNumeric}
                onChange={() => setIsCheckNumeric(!isCheckNumeric)}
                id={'auth-settings-numeric-chars-check'}
                data-apim-test={'auth-settings-numeric-chars-check'}
              />
            </div>
            <div className={classes.configCellContainer}>
              <FormTextField
                id={'auth-settings-numeric-chars'}
                data-apim-test={'auth-settings-numeric-chars'}
                name={intl.getI18nMessage('label.auth.numeric.characters')}
                value={numericChars}
                type={'number'}
                error={isNumericCharsError}
                handleChange={(value) => handleChange('numericChars', value, 2)}
                fieldContainerClass={classes.textConfigField}
                disabled={!isCheckNumeric}
                helperText={intl.getI18nMessage('label.auth.chars.help.text')}
              />
            </div>
            <div className={classes.configCheckContainer}>
              <Checkbox
                checked={isCheckSpecial}
                onChange={() => setIsCheckSpecial(!isCheckSpecial)}
                id={'auth-settings-special-chars-check'}
                data-apim-test={'auth-settings-special-chars-check'}
              />
            </div>
            <div className={classes.configCellContainer}>
              <FormTextField
                id={'auth-settings-special-chars'}
                data-apim-test={'auth-settings-special-chars'}
                name={intl.getI18nMessage('label.auth.special.characters')}
                value={specialChars}
                type={'number'}
                error={isSpecialCharsError}
                handleChange={(value) => handleChange('specialChars', value, 2)}
                fieldContainerClass={classes.textConfigField}
                helperText={intl.getI18nMessage('label.auth.special.characters.help.text')}
                disabled={!isCheckSpecial}
              />
            </div>

           <div className={classes.configContainer} />
            <Typography
              variant="h4"
              id="auth-settings-config-account-title"
              data-apim-test="auth-settings-config-account-title"
            >
              {getI18nFormattedMessage('label.auth.account.lockout')}
            </Typography>
            <div className={classes.configCheckContainer}>
              <Checkbox
                checked={isCheckMaxFailed}
                onChange={() => setIsCheckMaxFailed(!isCheckMaxFailed)}
                id={'auth-settings-max-failed-attempts-check'}
                data-apim-test={'auth-settings-max-failed-attempts-check'}
              />
            </div>
            <div className={classes.configCellContainer}>
              <FormTextField
                id={'auth-settings-max-failed-attempts'}
                data-apim-test={'auth-settings-max-failed-attempts'}
                name={intl.getI18nMessage('label.auth.maximum.failed.attempts')}
                value={maxFailedAttempts}
                type={'number'}
                error={isMaxFailedAttemptsError}
                handleChange={(value) => handleChange('maxFailedAttempts', value, 2)}
                fieldContainerClass={classes.textConfigField}
                helperText={intl.getI18nMessage('label.auth.maximum.failed.attempts.help.text')}
                disabled={!isCheckMaxFailed}
              />
            </div>

          <div className={classes.configContainer} />
          <Typography
            variant="h4"
            id="auth-settings-config-password-title"
            data-apim-test="auth-settings-config-password-title"
          >
            {getI18nFormattedMessage('label.auth.password.change')}
          </Typography>
          <div className={classes.configCheckContainer}>
            <Checkbox
              checked={isCheckPasswordExpiry}
              onChange={() => setIsCheckPasswordExpiry(!isCheckPasswordExpiry)}
              id={'auth-settings-password-expiry-check'}
              data-apim-test={'auth-settings-password-expiry-check'}
            />
          </div>
          <div className={classes.configCellContainer}>
            <FormTextField
              id={'auth-settings-password-expiry'}
              data-apim-test={'auth-settings-password-expiry'}
              name={intl.getI18nMessage('label.auth.password.expiry')}
              value={passwordExpiry}
              type={'number'}
              error={isPasswordExpiryError}
              handleChange={(value) => handleChange('passwordExpiry', value, 2)}
              fieldContainerClass={classes.textConfigField}
              helperText={intl.getI18nMessage('label.auth.password.expiry.help.text')}
              disabled={!isCheckPasswordExpiry}
            />
          </div>
          <div className={classes.configCheckContainer}>
            <Checkbox
              checked={isCheckPasswordHistory}
              onChange={() => setIsCheckPasswordHistory(!isCheckPasswordHistory)}
              id={'auth-settings-password-history-check'}
              data-apim-test={'auth-settings-password-history-check'}
            />
          </div>
          <div className={classes.configCellContainer}>
            <FormTextField
              id={'auth-settings-password-history'}
              data-apim-test={'auth-settings-password-history'}
              name={intl.getI18nMessage('label.auth.password.history')}
              value={passwordHistory}
              type={'number'}
              error={isPasswordHistoryError}
              handleChange={(value) => handleChange('passwordHistory', value, 2)}
              fieldContainerClass={classes.textConfigField}
              helperText={intl.getI18nMessage('label.auth.password.history.help.text')}
              disabled={!isCheckPasswordHistory}
            />
          </div>
          <div className={classes.configCheckContainer}>
            <Checkbox
              checked={isCheckPasswordLimit}
              onChange={() => setIsCheckPasswordLimit(!isCheckPasswordLimit)}
              id={'auth-settings-password-attempt-limit-check'}
              data-apim-test={'auth-settings-password-attempt-limit-check'}
            />
          </div>
          <div className={classes.configCellContainer}>
            <FormTextField
              id={'auth-settings-password-attempt-limit'}
              data-apim-test={'auth-settings-password-attempt-limit'}
              name={intl.getI18nMessage('label.auth.forgot.password.attempt.limit')}
              value={passwordLimit}
              type={'number'}
              error={isPasswordLimitError}
              handleChange={(value) => handleChange('passwordLimit', value, 2)}
              fieldContainerClass={classes.textConfigField}
              helperText={intl.getI18nMessage('label.auth.forgot.password.attempt.limit.help.text')}
              disabled={!isCheckPasswordLimit}
            />
          </div>
          <div className={classes.configContainer} />
          <div className={classes.switchContainer}>
            <Typography variant="h4" className={classes.sslEnabledLabel}>
              {intl.getI18nMessage('label.auth.enhanced.security')}
            </Typography>
            <Switch
              id={'auth-settings-config-security'}
              data-apim-test={'auth-settings-config-security'}
              checked={enhancedSecurity}
              onChange={() => {setEnhancedSecurity(!enhancedSecurity)}}
            />
          </div>
          <hr className={classes.hr}/>
          <FormHelperText>
            <div className={classes.helperBodyText}>
              {intl.getI18nMessage('label.auth.enhanced.security.help.text')}
            </div>
          </FormHelperText>
        </Grid>
      </Grid>
      <FormActionButtons
        onNextClick={onSave}
        nextText={getI18nFormattedMessage('label.save.button')}
        onCancelClick={onCancel}
        isDisabledAdditional={isSaveDisabled}
        id={'auth-settings-config-buttons'}
        data-apim-test={'auth-settings-config-buttons'}
      />
    </div>
  )
}

Configuration.propTypes = {
  classes: shape({}),
  errors: arrayOf({}),
  assets: shape({}),
  location: shape({}),
  uuid: string,
  notifyMessages: func,
  isProviderNameUnique: bool,
  checkProviderNameUnique: func,
  authDetails: shape({}),
  setAuthSettings: func,
  onCancelDetails: func,
  setStateTabs: func,
  tabValue: string,
  saveAuthScheme: func,
  updateAuthScheme: func,
  push: func,
  hasAttributeMappingFields: func,
  hasProviderConfigFields: func,
};

const mapStateToProps = (state) => ({
  isProviderNameUnique: getIsProviderNameUnique(state),
});

const mapDispatchToProps = {
  checkProviderNameUnique,
  setAuthSettings,
  saveAuthScheme,
  updateAuthScheme,
};

export default compose(
withStyles(styles),
connect(mapStateToProps, mapDispatchToProps),
)(Configuration);
